import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Gift, Zap, Info, Server } from "lucide-react";

/**
 * PromoCarousel.jsx
 * Rotating promo window with consistent height to avoid layout shifts.
 */

const DURATION_MS = 4000; // 4 seconds per slide

// inline markdown → HTML for **bold**
const mdInline = (s = "") => s.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");

const promos = [
  {
    id: "epycvds-5",
    tag: "NEW PACKAGE",
    title: "🚀 EPYCVDS-5 just launched: FREE +1.5GB RAM!",
    body: [
      "Optimized performance for large projects: high-efficiency, 99.99% uptime.",
      "Sign up today and receive an **extra 1.5GB RAM** free for the first month.",
      "Perfect for web apps, microservices, and long-running background tasks.",
    ],
    ctaLabel: "Explore EPYCVDS-5",
    ctaHref: "/pricing/epycvds-5",
    icon: Server,
  },
  {
    id: "bf",
    tag: "LIMITED TIME",
    title: "🔥 Black Friday Mega Sale",
    body: [
      "Save up to **60%** on VPS credits.",
      "Top up your balance between Nov 24–30 and receive automatic bonuses.",
      "Don’t miss this once-a-year deal!",
    ],
    ctaLabel: "Grab the Deal",
    ctaHref: "/billing/top-up",
    icon: Gift,
  },
  {
    id: "autoscale",
    tag: "FEATURE UPDATE",
    title: "⚡ Auto-Scaling VPS is here",
    body: [
      "Your instances scale automatically based on real-time demand.",
      "Define min/max replicas and CPU targets—no more manual tuning.",
      "Keep your services responsive and cost-efficient.",
    ],
    ctaLabel: "Enable Auto-Scale",
    ctaHref: "/features/autoscale",
    icon: Zap,
  },
  {
    id: "vps-service",
    tag: "WHY US",
    title: "💻 Reliable VPS Hosting You Can Trust",
    body: [
      "Blazing-fast SSD storage, enterprise-grade CPUs, and DDoS protection included.",
      "Affordable pricing with 24/7 expert support.",
      "Join thousands of developers who trust our VPS services every day.",
    ],
    ctaLabel: "See All Plans",
    ctaHref: "/pricing",
    icon: Info,
  },
];

export default function PromoCarousel() {
  const [index, setIndex] = useState(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setIndex((prev) => (prev + 1) % promos.length);
    }, DURATION_MS);
    return () => clearInterval(intervalRef.current);
  }, []);

  const promo = promos[index];
  const Icon = promo.icon;

  return (
    <div
      className="
        relative w-full mt-4 rounded-2xl overflow-hidden
        bg-white text-gray-900 shadow-md ring-1 ring-black/5
        dark:bg-zinc-900 dark:text-gray-100 dark:border dark:border-white/10 dark:shadow-xl dark:ring-0
      "
    >
      <div className="p-6 min-h-[240px] flex items-start">
        <AnimatePresence mode="wait">
          <motion.div
            key={promo.id}
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -40 }}
            transition={{ duration: 0.5 }}
            className="w-full"
          >
            <div className="flex items-start gap-3">
              <Icon className="h-8 w-8 shrink-0 text-indigo-600 dark:text-indigo-400" />
              <div className="flex-1">
                <span
                  className="
                    inline-block rounded-full px-2 py-0.5 text-xs font-semibold
                    bg-indigo-50 text-indigo-700
                    dark:bg-indigo-500/20 dark:text-indigo-300
                  "
                >
                  {promo.tag}
                </span>

                <h3 className="mt-1 text-lg font-bold text-gray-900 dark:text-gray-100">
                  {promo.title}
                </h3>

                <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-gray-700 dark:text-gray-300">
                  {promo.body.map((line, i) => (
                    <li key={i} dangerouslySetInnerHTML={{ __html: mdInline(line) }} />
                  ))}
                </ul>

                {promo.ctaHref && (
                  <a
                    href={promo.ctaHref}
                    className="
                      mt-3 inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-medium
                      bg-indigo-600 text-white shadow hover:bg-indigo-700
                      focus:outline-none focus:ring-2 focus:ring-indigo-500
                    "
                  >
                    {promo.ctaLabel}
                  </a>
                )}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Dots indicator */}
      <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-2">
        {promos.map((p, i) => (
          <button
            key={p.id}
            onClick={() => setIndex(i)}
            aria-label={`Show slide ${i + 1}`}
            className={`
              h-2.5 w-2.5 rounded-full transition
              ${i === index
                ? "bg-indigo-600 dark:bg-indigo-400"
                : "bg-gray-300 dark:bg-white/30"}
            `}
          />
        ))}
      </div>
    </div>
  );
}
